import { ISearchOrgResult } from '../../../types/org';
import { phoneNumberAutoFormat2 } from '../../../utils/format-phone';

export class SearchOrgResultModel implements ISearchOrgResult {
  id!: ISearchOrgResult['id'];
  orgName!: ISearchOrgResult['orgName'];
  addressUsages!: ISearchOrgResult['addressUsages'];
  contactPhones!: ISearchOrgResult['contactPhones'];
  desc!: ISearchOrgResult['desc'];
  restricted!: ISearchOrgResult['restricted'];

  constructor(src: ISearchOrgResult) {
    Object.assign(this, src);
  }

  // Build and return a sorted list of normalized address objects to be used by both getters
  private buildSortedAddressLines(): { id: string | number | undefined; address: string; expired: boolean }[] {
    if (!this.addressUsages || this.addressUsages.length === 0) return [];
    const lines = this.addressUsages
      .map(a => {
        const address = `${(a.addressTxt || '').trim()} ${(a.addressTxt2 || '').trim()}`
          .replace(/\s+/g, ' ')
          .trim();
        return {
          id: a.addressId,
          address,
          expired: !!a.addressExpired,
        };
      })
      .filter(l => l.address.length > 0);
    return lines.sort((a, b) => a.address.localeCompare(b.address));
  }

  // Returns normalized address string with line breaks between multiple addresses to be passed in as a sortable source
  get normalizedAddress(): string {
    const lines = this.buildSortedAddressLines();
    return lines.map(l => l.address).join('\n');
  }

  // Returns array of expired addresses for rows with multiple addresses so the icon only displays for specific expired ones in the list
  get addressLines(): { id: string | number | undefined; text: string; expired: boolean }[] {
    const lines = this.buildSortedAddressLines();
    return lines.map(l => ({ id: l.id, text: l.address, expired: l.expired }));
  }

  get normalizedPhone(): string {
    if (!this.contactPhones || this.contactPhones.length === 0) return '';
    return this.contactPhones
      .map(p => {
        const countryCode = (p.countryCode || '').trim();
        const areaCode = (p.areaCode || '').trim();
        const localNumber = (p.localNumber || '').replace(/\D/g, '');
        const extension = (p.extension || '').trim();
        const formatted = phoneNumberAutoFormat2(countryCode, areaCode, localNumber, extension);
        return formatted.trim();
      })
      .filter(s => s.length > 0)
      .sort((a, b) => a.localeCompare(b))
      .join('\n');
  }
}